import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import {
	Link,
	Outlet,
	createFileRoute,
	useLocation,
} from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/docs")({
	component: DocsLayout,
});

type NavChild = { href: string; label: string };
type NavItem = { href: string; label: string; children?: NavChild[] };
type NavSection = { title: string; items: NavItem[] };

function DocsLayout() {
	const location = useLocation();
	const { t } = useTranslation("docs");
	const currentPath = location.pathname;

	const NAV_ITEMS: NavSection[] = [
		{
			title: t("phase1_section"),
			items: [
				{ href: "/docs/phase1", label: t("phase1_overview") },
				{
					href: "/docs/phase1/procurement",
					label: t("phase1_procurement"),
				},
				{ href: "/docs/phase1/sales", label: t("phase1_sales") },
				{ href: "/docs/phase1/admin", label: t("phase1_admin") },
			],
		},
		{
			title: t("portal_api"),
			items: [
				{
					href: "/docs/portal/endpoints",
					label: t("endpoints"),
					children: [
						{ href: "#quickstart", label: "Быстрый старт" },
						{ href: "#auth", label: "Аутентификация" },
						{ href: "#search", label: "Поиск" },
						{ href: "#products", label: "CRUD продуктов" },
						{ href: "#catalog", label: "B2B-каталог" },
						{ href: "#schema", label: "Схема данных" },
						{ href: "#keys", label: "Ключи API" },
						{ href: "#limits", label: "Лимиты" },
						{ href: "#errors", label: "Ошибки" },
					],
				},
				{
					href: "/docs/portal/widget",
					label: t("widget"),
					children: [
						{ href: "#installation", label: "Установка" },
						{ href: "#params", label: "Параметры" },
						{ href: "#events", label: "События" },
						{ href: "#tracker", label: "Трекер" },
					],
				},
			],
		},
		{
			title: t("plugins_section"),
			items: [
				{
					href: "/docs/plugins",
					label: t("overview"),
					children: [
						{ href: "#architecture", label: "Архитектура" },
						{ href: "#extension-points", label: "Точки расширения" },
						{ href: "#example", label: "Пример" },
					],
				},
				{
					href: "/docs/plugins/getting-started",
					label: t("getting_started"),
					children: [
						{ href: "#requirements", label: "Требования" },
						{ href: "#scaffold", label: "Создание проекта" },
						{ href: "#code", label: "Код" },
						{ href: "#build", label: "Сборка" },
						{ href: "#publish", label: "Публикация" },
					],
				},
				{
					href: "/docs/plugins/sdk-reference",
					label: t("sdk_reference"),
					children: [
						{ href: "#macros", label: "Макросы" },
						{ href: "#types-enrichment", label: "Типы: обогащение" },
						{ href: "#types-pipeline", label: "Типы: пайплайн" },
						{ href: "#host-functions", label: "Host-функции" },
						{ href: "#error-handling", label: "Обработка ошибок" },
					],
				},
				{
					href: "/docs/plugins/capabilities",
					label: t("capabilities"),
					children: [
						{ href: "#process", label: "Процесс" },
						{ href: "#available", label: "Доступные" },
						{ href: "#limits", label: "Лимиты" },
					],
				},
				{
					href: "/docs/plugins/publishing",
					label: t("publishing"),
					children: [
						{ href: "#checklist", label: "Чеклист" },
						{ href: "#via-api", label: "Через API" },
						{ href: "#via-ui", label: "Через UI" },
					],
				},
			],
		},
	];

	const findCurrentLabel = (path: string): string | undefined => {
		for (const sec of NAV_ITEMS)
			for (const item of sec.items) if (item.href === path) return item.label;
		return undefined;
	};

	const findCurrentSectionRoot = (
		path: string,
	): { title: string; href: string } | undefined => {
		for (const sec of NAV_ITEMS)
			for (const item of sec.items)
				if (item.href === path)
					return { title: sec.title, href: sec.items[0]?.href ?? "/docs" };
		return undefined;
	};

	const sectionRoot = findCurrentSectionRoot(currentPath);
	const label = findCurrentLabel(currentPath);

	const crumbs = [
		{ label: t("title"), href: "/docs" },
		...(sectionRoot
			? [{ label: sectionRoot.title, href: sectionRoot.href }]
			: []),
		...(label && label !== t("overview") ? [{ label }] : []),
	];

	useBreadcrumbs(crumbs);

	return (
		<div className="flex h-full overflow-hidden">
			<aside className="w-52 shrink-0 border-r bg-muted/30 overflow-y-auto">
				<div className="sticky top-0 p-4">
					<nav className="space-y-5">
						{NAV_ITEMS.map((sec) => (
							<div key={sec.title}>
								<h3 className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-wider px-2 mb-1.5">
									{sec.title}
								</h3>
								<ul className="space-y-0.5">
									{sec.items.map((item) => {
										const isActive = currentPath === item.href;
										return (
											<li key={item.href}>
												<Link
													to={item.href as "/docs/plugins"}
													className={cn(
														"flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-md transition-colors",
														isActive
															? "bg-accent text-accent-foreground font-medium"
															: "text-muted-foreground hover:text-foreground hover:bg-accent/50",
													)}
												>
													<ChevronRight
														className={cn(
															"h-3 w-3 transition-transform shrink-0",
															isActive && "rotate-90",
														)}
													/>
													{item.label}
												</Link>
												{item.children && isActive && (
													<ul className="ml-5 mt-0.5 space-y-0 border-l border-border/50">
														{item.children.map((child) => (
															<li key={child.href}>
																<a
																	href={child.href}
																	className="block pl-3 py-1 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
																>
																	{child.label}
																</a>
															</li>
														))}
													</ul>
												)}
											</li>
										);
									})}
								</ul>
							</div>
						))}
					</nav>
				</div>
			</aside>

			<main className="flex-1 overflow-y-auto px-10 py-6">
				<div className="max-w-3xl mx-auto">
					<Outlet />
				</div>
			</main>
		</div>
	);
}
